import React from "react";
import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import api from "../../config/URL";
import { useNavigate } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

function CurriculumAdd({ onSuccess, curriculumOutletId, courseId }) {
  const [show, setShow] = useState(false);
  const [loadIndicator, setLoadIndicator] = useState(false);
  const userName = localStorage.getItem("userName");
  const [isModified, setIsModified] = useState(false);

  const navigate = useNavigate();
  const navigates = () => {
    navigate(-1);
  };

  const handleClose = () => {
    setShow(false);
    formik.resetForm();
  };

  const handleShow = () => {
    setShow(true);
    setIsModified(false);
  };

  const validationSchema = Yup.object({
    curriculumCode: Yup.string().required("*Curriculum Code is required"),
    lessonNo: Yup.string().required("*Lesson No is required"),
    status: Yup.string().required("*Status is required"),
    curriculumNo: Yup.string().required("*Curriculum No is required"),
    present: Yup.string().nullable().max(1500, "*Max 1500 characters"),
    absent: Yup.string().nullable().max(1500, "*Max 1500 characters"),
  });

  const formik = useFormik({
    initialValues: {
      curriculumCode: "",
      lessonNo: "",
      status: "",
      curriculumNo: "",
      present: "",
      absent: "",
      createdBy: userName,
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      setLoadIndicator(true);
      const payload = {
        curriculumOutletId: curriculumOutletId,
        curriculumCode: values.curriculumCode,
        lessonNo: values.lessonNo,
        status: values.status,
        curriculumNo: values.curriculumNo,
        courseId: courseId,
        templateSettings: {
          present: values.present || "", // Ensure empty string if null/undefined
          absent: values.absent || "", // Ensure empty string if null/undefined
        },
        createdBy: userName,
      };

      try {
        const response = await api.post(
          `/createCourseCurriculumCode/${curriculumOutletId}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        );
        if (response.status === 201) {
          toast.success(response.data.message);
          onSuccess();
          handleClose();
        } else {
          toast.error(response.data.message);
        }
      } catch (error) {
        toast.error(error.message || "Something went wrong");
      } finally {
        setLoadIndicator(false);
      }
    },
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validate: (values) => {
      if (
        Object.values(values).some(
          (value) => value && value.toString().trim() !== ""
        )
      ) {
        setIsModified(true);
      } else {
        setIsModified(false);
      }
    },
  });

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "blockquote",
    "list",
    "bullet",
    "link",
  ];

  const handlePresentChange = (value) => {
    formik.setFieldValue("present", value === "<p><br></p>" ? "" : value);
    formik.setFieldTouched("present", true);
  };

  const handleAbsentChange = (value) => {
    formik.setFieldValue("absent", value === "<p><br></p>" ? "" : value);
    formik.setFieldTouched("absent", true);
  };

  return (
    <>
      <div className="py-2 d-flex justify-content-end">
        <button
          type="button"
          onClick={navigates}
          className="btn btn-sm btn-border"
        >
          Back
        </button>
        &nbsp;&nbsp;
        <button
          type="button"
          className="btn btn-button btn-sm"
          onClick={handleShow}
        >
          Add <i className="bx bx-plus"></i>
        </button>
      </div>
      <Modal
        show={show}
        size="lg"
        onHide={handleClose}
        centered
        backdrop={isModified ? "static" : true}
        keyboard={isModified ? false : true}
      >
        <Modal.Header closeButton>
          <Modal.Title className="headColor">Add Curriculum</Modal.Title>
        </Modal.Header>
        <form
          onSubmit={formik.handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !formik.isSubmitting) {
              e.preventDefault();
            }
          }}
        >
          <Modal.Body>
            <div className="container">
              <div className="row py-4">
                {/* Lesson No */}
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Lesson No<span className="text-danger">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("lessonNo")}
                    className={`form-select ${
                      formik.touched.lessonNo && formik.errors.lessonNo
                        ? "is-invalid"
                        : ""
                    }`}
                  >
                    <option value=""></option>
                    {Array.from({ length: 150 }, (_, index) => (
                      <option key={index + 1} value={index + 1}>
                        {index + 1}
                      </option>
                    ))}
                  </select>
                  {formik.touched.lessonNo && formik.errors.lessonNo && (
                    <div className="invalid-feedback">
                      {formik.errors.lessonNo}
                    </div>
                  )}
                </div>

                {/* Curriculum Code */}
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Curriculum Code<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      formik.touched.curriculumCode &&
                      formik.errors.curriculumCode
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("curriculumCode")}
                  />
                  {formik.touched.curriculumCode &&
                    formik.errors.curriculumCode && (
                      <div className="invalid-feedback">
                        {formik.errors.curriculumCode}
                      </div>
                    )}
                </div>

                {/* Curriculum Number */}
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Curriculum Number<span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${
                      formik.touched.curriculumNo && formik.errors.curriculumNo
                        ? "is-invalid"
                        : ""
                    }`}
                    {...formik.getFieldProps("curriculumNo")}
                  />
                  {formik.touched.curriculumNo &&
                    formik.errors.curriculumNo && (
                      <div className="invalid-feedback">
                        {formik.errors.curriculumNo}
                      </div>
                    )}
                </div>

                {/* Status */}
                <div className="col-md-6 col-12 mb-2">
                  <label className="form-label">
                    Status<span className="text-danger">*</span>
                  </label>
                  <select
                    {...formik.getFieldProps("status")}
                    className={`form-select ${
                      formik.touched.status && formik.errors.status
                        ? "is-invalid"
                        : ""
                    }`}
                  >
                    <option value=""></option>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                  {formik.touched.status && formik.errors.status && (
                    <div className="invalid-feedback">
                      {formik.errors.status}
                    </div>
                  )}
                </div>

                {/* Present Message */}
                <div className="col-md-12 col-12 mb-2">
                  <label className="form-label">Present Message</label>
                  {/* <ReactQuill
                    value={formik.values.present}
                    onChange={handlePresentChange}
                    modules={modules}
                    formats={formats}
                    className={`${
                      formik.touched.present && formik.errors.present
                        ? "is-invalid"
                        : ""
                    }`}
                  /> */}
                  <textarea
                    name="present"
                    {...formik.getFieldProps("present")}
                    onKeyDown={(e) => e.stopPropagation()}
                    className={`form-control ${
                      formik.touched.present && formik.errors.present
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  
                  {formik.touched.present && formik.errors.present && (
                    <div className="invalid-feedback">
                      {formik.errors.present}
                    </div>
                  )}
                </div>

                {/* Absent Message */}
                <div className="col-md-12 col-12 mb-2">
                  <label className="form-label">Absent Message</label>
                  {/* <ReactQuill
                    value={formik.values.absent}
                    onChange={handleAbsentChange}
                    modules={modules}
                    formats={formats}
                    className={`${
                      formik.touched.absent && formik.errors.absent
                        ? "is-invalid"
                        : ""
                    }`}
                  /> */}
                  <textarea
                    name="absent"
                    {...formik.getFieldProps("absent")}
                    onKeyDown={(e) => e.stopPropagation()}
                    className={`form-control ${
                      formik.touched.absent && formik.errors.absent
                        ? "is-invalid"
                        : ""
                    }`}
                  />
                  {formik.touched.absent && formik.errors.absent && (
                    <div className="invalid-feedback">
                      {formik.errors.absent}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <Modal.Footer>
              <Button
                type="button"
                className="btn btn-sm btn-border bg-light text-dark"
                onClick={handleClose}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="btn btn-button btn-sm"
                disabled={loadIndicator}
              >
                {loadIndicator && (
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    aria-hidden="true"
                  ></span>
                )}
                Submit
              </Button>
            </Modal.Footer>
          </Modal.Body>
        </form>
      </Modal>
    </>
  );
}

export default CurriculumAdd;
